/**
 * Small building blocks shared by the Business Review tabs (pages/BusinessReviewPage.js).
 *
 *   <Section title subtitle right testId>…</Section>      a white card with a heading row
 *   <Metric entry={total.revenue} kind="money" label="Revenue" />   value + WoW / vs 4 wk / YoY line
 *   <Delta pct={6.2} />                                      "+6.2%" coloured (invert for lower-is-better)
 *   <TagChip tag="stock_out" />                              mover cause chip
 *   <ActionList actions={…} />                               suggested actions with ₹ impact and a link
 *   <Notes notes={[…]} />                                    the data-basis notes under a tab
 *   <BridgeChart bridge={…} />                               price / volume / mix / new / lost waterfall
 *   <Empty>…</Empty>, <Money v />, <Num v />, <Pct v />      formatting helpers
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight } from 'lucide-react';
import { number, shortMoney, signedPct } from '../sales/format';
import { ACTION_LABELS, TAG_LABELS, TAG_TONES } from './reviewApi';

export const Money = ({ v }) => <span className="tabular-nums">{v == null ? '—' : shortMoney(v)}</span>;
export const Num = ({ v }) => <span className="tabular-nums">{v == null ? '—' : number(v)}</span>;
export const Pct = ({ v, digits = 1 }) => <span className="tabular-nums">{v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(digits)}%`}</span>;

export const Section = ({ title, subtitle, right, children, className = '', testId }) => (
  <section className={`min-w-0 rounded-xl border border-gray-200 bg-white ${className}`} data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 px-4 py-3">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
      </div>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

export const Empty = ({ children }) => <p className="py-4 text-center text-sm text-gray-500">{children}</p>;

const toneFor = (pct, invert) => {
  if (pct == null) return 'text-gray-400';
  if (pct === 0) return 'text-gray-500';
  const good = pct > 0 ? !invert : invert;
  return good ? 'text-green-700' : 'text-red-700';
};

/** A percentage change; `points` shows "+1.2 pts" (used for discount / rate metrics). */
export const Delta = ({ pct, invert = false, points = false, className = '' }) => {
  if (pct == null) return <span className={`text-gray-400 ${className}`}>—</span>;
  const text = points ? `${pct > 0 ? '+' : pct < 0 ? '−' : ''}${Math.abs(Number(pct)).toFixed(1)} pts` : signedPct(Number(pct));
  return <span className={`tabular-nums ${toneFor(Number(pct), invert)} ${className}`}>{text}</span>;
};

const formatValue = (value, kind) => {
  if (value == null) return '—';
  if (kind === 'money') return shortMoney(value);
  if (kind === 'percent') return `${Number(value).toFixed(1)}%`;
  return number(value);
};

/** One KPI tile from a review metric entry {value, previous, wow_pct, vs4wk_pct, yoy_pct}. */
export const Metric = ({ label, entry, kind = 'money', invert = false, points = false, hint, footnote, testId }) => (
  <div className="min-w-0 rounded-xl border border-gray-200 bg-white p-3 shadow-sm" data-testid={testId} title={hint}>
    <span className="block text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</span>
    <span className="mt-1 block text-xl font-semibold tabular-nums text-gray-900">{formatValue(entry?.value, kind)}</span>
    {entry && (
      <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
        {'wow_pct' in entry && <span>WoW <Delta pct={entry.wow_pct} invert={invert} points={points} /></span>}
        {'vs4wk_pct' in entry && <span>4 wk <Delta pct={entry.vs4wk_pct} invert={invert} points={points} /></span>}
        {'yoy_pct' in entry && entry.yoy_pct != null && <span>YoY <Delta pct={entry.yoy_pct} invert={invert} points={points} /></span>}
        {entry.previous != null && !('wow_pct' in entry) && <span>last week {formatValue(entry.previous, kind)}</span>}
      </span>
    )}
    {footnote && <span className="mt-1 block text-[11px] text-gray-500">{footnote}</span>}
  </div>
);

export const TagChip = ({ tag }) => (
  <span className={`inline-flex whitespace-nowrap rounded-full px-1.5 py-0 text-[11px] font-medium ring-1 ring-inset ${TAG_TONES[tag] || TAG_TONES.demand}`}>
    {TAG_LABELS[tag] || tag}
  </span>
);

export const ActionList = ({ actions = [], testId = 'review-actions' }) => {
  if (!actions.length) return <Empty>No actions suggested for this week.</Empty>;
  return (
    <ol className="divide-y divide-gray-100" data-testid={testId}>
      {actions.map((action, index) => (
        <li key={`${action.type}-${action.sku_code || ''}-${action.platform || ''}-${index}`} className="flex flex-wrap items-start justify-between gap-2 py-2 text-sm">
          <div className="min-w-0 flex-1">
            <span className="mr-2 inline-flex rounded-full bg-gray-100 px-2 py-0 text-[11px] font-medium text-gray-700">{ACTION_LABELS[action.type] || action.type}</span>
            <span className="text-gray-900">{action.title}</span>
            <span className="block text-xs text-gray-500">{action.basis}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-semibold tabular-nums text-gray-900"><Money v={action.impact_inr} /></span>
            {action.link && (
              <Link to={action.link} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                Open <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </Link>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

export const Notes = ({ notes = [] }) => (notes.length ? (
  <ul className="space-y-0.5 text-[11px] leading-snug text-gray-500" data-testid="review-notes">
    {notes.filter(Boolean).map(note => <li key={note}>· {note}</li>)}
  </ul>
) : null);

const BRIDGE_STEPS = [['price', 'Price'], ['volume', 'Volume'], ['mix', 'Mix'], ['new', 'New'], ['lost', 'Lost'], ['other', 'Other']];

/** Waterfall from last week's revenue to this week's: each step floats on an invisible base. */
export const BridgeChart = ({ bridge, height = 220 }) => {
  if (!bridge) return null;
  const rows = [{ name: 'Last week', base: 0, value: bridge.previous, kind: 'total' }];
  let level = bridge.previous || 0;
  BRIDGE_STEPS.forEach(([key, label]) => {
    const delta = Number(bridge[key]) || 0;
    const next = level + delta;
    rows.push({ name: label, base: Math.min(level, next), value: Math.abs(delta), delta, kind: delta >= 0 ? 'up' : 'down' });
    level = next;
  });
  rows.push({ name: 'This week', base: 0, value: bridge.current, kind: 'total' });
  const color = row => (row.kind === 'total' ? '#1f2937' : row.kind === 'up' ? '#16a34a' : '#dc2626');
  const label = `Revenue bridge: last week ${shortMoney(bridge.previous)}; ${BRIDGE_STEPS.map(([k, l]) => `${l} ${shortMoney(bridge[k])}`).join(', ')}; this week ${shortMoney(bridge.current)}`;
  return (
    <div role="img" aria-label={label} style={{ height }} data-testid="review-bridge-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 6" vertical={false} stroke="#e5e7eb" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis tickFormatter={shortMoney} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={64} />
          <Tooltip cursor={{ fill: '#f1f5f9' }} formatter={(value, name, item) => (name === 'base' ? null : [shortMoney(item.payload.kind === 'total' ? value : item.payload.delta), item.payload.name])} />
          <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="value" stackId="w" isAnimationActive={false} radius={[3, 3, 0, 0]}>
            {rows.map(row => <Cell key={row.name} fill={color(row)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Plain data table: columns [{ key, label, align, render }] over rows; `rowKey` names the key field. */
export const DataTable = ({ columns, rows, rowKey = 'sku_code', caption, testId, empty = 'Nothing here this week.' }) => {
  if (!rows?.length) return <Empty>{empty}</Empty>;
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm" data-testid={testId}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>{columns.map(col => <th key={col.key} scope="col" className={`px-3 py-2 font-medium ${col.align === 'right' ? 'text-right' : 'text-left'}`}>{col.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={typeof rowKey === 'function' ? rowKey(row, index) : `${row[rowKey]}-${index}`} className="border-t border-gray-100">
              {columns.map(col => (
                <td key={col.key} className={`px-3 py-2 ${col.align === 'right' ? 'text-right tabular-nums' : ''} ${col.className || ''}`}>
                  {col.render ? col.render(row) : row[col.key] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const SkuCell = ({ row }) => (
  <span className="block min-w-0">
    <Link to={`/sales/skus?sku=${encodeURIComponent(row.sku_code)}`} className="font-mono text-xs font-medium text-blue-700 hover:underline">{row.sku_code}</Link>
    {row.product_name && <span className="block max-w-[260px] truncate text-xs text-gray-500" title={row.product_name}>{row.product_name}</span>}
  </span>
);
